import React, { Component } from 'react';
import { error as logError, reportError } from '../../utils/logger';
import { isStrictMode } from '../../utils/strictMode';
export interface ErrorFallbackProps {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  onReset: () => void;
}
interface BaseErrorBoundaryProps {
  children: React.ReactNode;
  /** Prefix for log messages (e.g. "Global", "Practice Session", "Question Renderer") */
  logPrefix: string;
  /** Extra context to include in log output */
  logContext?: Record<string, unknown>;
  /** When this key changes, the error state resets automatically */
  resetKey?: string | number;
  /** Called after error state is reset */
  onReset?: () => void;
  /** Render function for the fallback UI */
  fallback: (props: ErrorFallbackProps) => React.ReactNode;
  /**
   * If true, this boundary respects the session-scoped strict-mode toggle and
   * lets errors propagate when strict mode is enabled. Opt-in per boundary so
   * the debugging tool (intended for question renderers) does not also disable
   * higher-level boundaries like the Global one.
   * @default false
   */
  respectStrictMode?: boolean;
}
interface BaseErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}
/**
 * BaseErrorBoundary — Reusable error boundary with shared lifecycle logic.
 *
 * Handles: constructor, getDerivedStateFromError, componentDidCatch,
 * isStrictMode bypass, resetKey-based auto-reset, and dev-mode logging.
 *
 * Each specific boundary (Global, Practice, Question) wraps this with
 * its own fallback UI via the `fallback` render prop.
 */
export class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logError(`${this.props.logPrefix} Error:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      ...this.props.logContext
    });
    // F-REL-1: forward to telemetry seam. The seam is a no-op locally
    // but allows external wiring (e.g. Sentry) without touching boundaries.
    reportError(error, {
      boundary: this.props.logPrefix,
      componentStack: errorInfo.componentStack ?? undefined,
      questionId: typeof this.props.logContext?.questionId === 'string' ? this.props.logContext.questionId : undefined,
      extra: this.props.logContext
    });
    this.setState({
      errorInfo
    });
  }
  componentDidUpdate(prevProps: BaseErrorBoundaryProps): void {
    if (this.props.resetKey !== undefined && prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    }
  }
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    this.props.onReset?.();
  };
  render(): React.ReactNode {
    if (this.props.respectStrictMode && isStrictMode()) {
      return this.props.children;
    }
    if (this.state.hasError && this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        errorInfo: this.state.errorInfo,
        onReset: this.handleReset
      });
    }
    return this.props.children;
  }
}